import { transient, autoinject, observable, computedFrom, useView } from "aurelia-framework";
import { DynamicPropertiesManager } from "../../../dynamic-properties-manager";
import { ChildComponent } from "../../../child-component";
import { I18N } from "aurelia-i18n";
import { NotificationClientService, NotificationType } from "../../../../../../services/basics/notification-client.service";
import { RightShipDialogServiceConstants } from "../../../../../../constants/basics/rightship-dialog.constants";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { ManageVesselService } from "../../../../../../services/vessels/manage-vessel.service";
import { SourceType, DataType, ItemSource } from "../../../../../../models/queries/queries.models";
import { BaseUscgData, VesselCapSurveyClientModel, VesselClassCertificateClientModel, VesselClassSurveyClientModel, VesselMouClientModel, VesselMousEnum, VesselThirdPartyInspectionClientModel } from "../../../../../../models/vessels/vessel.models";
import * as moment from "moment";
import { DataGrid } from "../../../../../components/data-grid/data-grid";
import { UnitOfWorkAction } from "../../../../../../utilities/unit-of-work-observer";
import { ArrayUtils } from "../../../../../../utilities/arrays";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { Reflection } from "../../../../../../utilities/reflection";
import { SignalClientService } from "@rightship-tech/common-ui/services/basic/signal-client.service";
import { SignalFunctions } from "../../../../../../services/basics/signal-functions";
import { UserClaimsService } from "../../../../../../services/authentication/user-claims.service";
import { Claims } from "../../../../../../constants/authentication/claims";
import * as Enumerable from "linq";
import { RightshipDataGrid } from "../../../../../components/rightship-data-grid/rightship-data-grid";
import { UserClientService } from "../../../../../../services/authentication/user-client.service";

@autoinject()
@transient()
@useView('views/pages/modules/vessels/steps/vessel-details-tabs/vessel-details-vetting-attributes.html')
export class VesselDetailsVettingAttributes extends ChildComponent<any> {

    public viewModel: DynamicPropertiesManager;
    public model: any;
    public capSurveysDataGridVM: DataGrid;
    lastLodDate: string;
    lastCotpDate: string;

    lastClassSurveyDate: string;
    lastClassCertificateDate: string;

    public uscgLodItemSource: ItemSource;
    public uscgCotpItemSource: ItemSource;

    public capSurveysItemSource: ItemSource;
    public classSurveysItemSource: ItemSource;
    public classCertificatesItemSource: ItemSource;

    public thirdPartyInspectionsDataGridVM: DataGrid;
    public thirdPartyInspectionsItemSource: ItemSource;

    public mousItemSource: ItemSource;
    public mouClearanceLogsItemSource: ItemSource;

    @observable()
    public mouClearanceLogsDataGridVM: DataGrid;

    public classSurveysDataGridVM: RightshipDataGrid;
    public classCertificatesDataGridVM: RightshipDataGrid;

    private _classSurveySettingKey: string = "skipDeleteWarning-ClassSurvey";
    private _classCertificateSettingKey: string = "skipDeleteWarning-ClassCertificate";

    constructor(viewModel: DynamicPropertiesManager, notificationService: NotificationClientService, i18N: I18N,
        private dialogClient: DialogClientService, private vesselService: ManageVesselService,
        private signalClientService: SignalClientService, private userClaimsService: UserClaimsService,
        private userClientService: UserClientService) {
        super(notificationService, i18N);
        this.viewModel = viewModel;
        this.viewModel.hasSave = false;
        this.dialogClient = dialogClient;
    }


    public canAddThirdPartyInspections() {
        return this.userClaimsService.hasPermission(Claims.CanAddThirdPartyInspections)
    }

    public activate(model: any) {
        this.model = model;

        if (this.model && this.model.id) {

            this.loadUscgData(this.model.id);

            this.vesselService.getVesselSurveysAndCertificates(this.model.id).then(vcs => {
                this.setCAPSurveysItemSource(vcs.vesselCapSurveys);
                this.setClassCertificatesItemSource(vcs.vesselClassCertificates);
                this.setClassSurveysItemSource(vcs.vesselClassSurveys);
            }).catch(() => {
                this.notificationService.show("Error", "Error getting CAP Surveys / Certificates", NotificationType.Error);
            });

            this.vesselService.getVesselThirdPartyInspections(this.model.id).then(s => {
                this.setThirdPartyInspectionsItemSource(s)
            }).catch(() => {
                this.notificationService.show("Error", "Error getting Third party inspections", NotificationType.Error);
            });

            this.vesselService.getVesselMous(this.model.id).then(m => {
                this.setMousItemSource(m.vesselMous);
                this.setMouClearanceLogsItemSource();
                this.signalClientService.subscribe(SignalFunctions.mouCleared, this.handleMousCleared);
            }).catch(() => {
                this.notificationService.show("Error", "Error getting MOU Info", NotificationType.Error);
            });
        }
    }

    loadingUscgData: boolean = false;
    private loadUscgData(vesselId) {
        this.loadingUscgData = true;
        this.vesselService.getVesselUscgData(vesselId, true, true).then(uscgData => {
            this.uscgLodItemSource = this.getUscgItemSource(uscgData.lods);
            this.lastLodDate = !uscgData.lods || uscgData.lods.length == 0 ? 'N/A' : moment(Enumerable.from(uscgData.lods).orderByDescending(x => x.createdDate).first().createdDate).format("DD/MM/YYYY HH:mm");

            this.uscgCotpItemSource = this.getUscgItemSource(uscgData.cotps);
            this.lastCotpDate = !uscgData.cotps || uscgData.cotps.length == 0 ? 'N/A' : moment(Enumerable.from(uscgData.cotps).orderByDescending(x => x.createdDate).first().createdDate).format("DD/MM/YYYY HH:mm");
            this.loadingUscgData = false;

        }).catch(() => {
            this.loadingUscgData = false;
            this.notificationService.show("Error", "Error getting USCG data", NotificationType.Error);
        });
    }

    private setClassSurveysItemSource(classSurveys: VesselClassSurveyClientModel[]) {
        const columns = [];
        columns.push(
            {
                columnOrder: 1,
                sortOrder: 1,
                dataType: DataType.Default,
                isSortable: true,
                name: 'classificationSociety',
                title: 'Class'
            },
            {
                columnOrder: 1,
                sortOrder: 1,
                dataType: DataType.Default,
                isSortable: true,
                name: 'description',
                title: 'Description'
            },
            {
                columnOrder: 1,
                sortOrder: 1,
                dataType: DataType.Date,
                isSortable: true,
                name: 'lastInspected',
                title: 'Last Inspected',
                valueConverter: (value) => value ? moment(value).format("DD/MM/YYYY") : ""
            },
            {
                columnOrder: 1,
                sortOrder: 1,
                dataType: DataType.Date,
                isSortable: true,
                name: 'dueDate',
                title: 'Due Date',
                valueConverter: (value) => value ? moment(value).format("DD/MM/YYYY") : ""
            },
            {
                columnOrder: 1,
                sortOrder: 1,
                dataType: DataType.Date,
                isSortable: true,
                name: 'dueDateRangeFrom',
                title: 'Due Date From',
                valueConverter: (value) => value ? moment(value).format("DD/MM/YYYY") : ""
            },
            {
                columnOrder: 1,
                sortOrder: 1,
                dataType: DataType.Date,
                isSortable: true,
                name: 'dueDateRangeTo',
                title: 'Due Date To',
                valueConverter: (value) => value ? moment(value).format("DD/MM/YYYY") : ""
            },
            {
                columnOrder: 1,
                sortOrder: 1,
                dataType: DataType.Date,
                isSortable: true,
                name: 'postponedUntilDate',
                title: 'Postponed Until',
                valueConverter: (value) => value ? moment(value).format("DD/MM/YYYY") : ""
            },
            {
                columnOrder: 10,
                sortOrder: 1,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                name: "menu",
                value: `<div class="actions row justify-content-end align-items-middle">
                            <list-menu hide-menu-border="true" menu-items.bind="menuItems(item)" if.bind="menuItems(item).length > 0">
                                <template replace-part="menu-item">
                                    <div class="row nowrap align-items-middle" click.trigger="menuItem.click($event, item)">
                                        <span class="text">\${menuItem.item.text}</span>
                                    </div>  
                                </template>
                            </list-menu>
                        </div>`,
                parameter: {
                    item: null,
                    menuItems: () => {
                        return [
                            {
                                isVisible: true,
                                click: ($event, item) => {
                                    if ($event)
                                        $event.stopPropagation();
                                    this.dialogClient.show(RightShipDialogServiceConstants.URLs.AddVesselClassSurveyDialog, {
                                        onComplete: this.handleSaveClassSurveyComplete.bind(this),
                                        id: item ? item.vesselClassSurveyId : null,
                                        vesselId: this.model.id ? this.model.id : null
                                    }, true, false, false, 'right');
                                },
                                item: { text: "Edit" },
                                classList: ''
                            },
                            {
                                isVisible: true,
                                click: async ($event, item) => {
                                    if ($event && $event.stopPropagation)
                                        $event.stopPropagation();

                                    const deleteClassSurvey = async (dontShowWarningAgain: boolean) => {
                                        const response = await this.vesselService.deleteClassSurvey(item.vesselClassSurveyId, dontShowWarningAgain);
                                        if (response.isSuccess) {
                                            const index = this.classSurveysItemSource.source.indexOf(item);
                                            this.classSurveysItemSource.source.splice(index, 1);
                                        }
                                    }
                                    if(this.userHasSetting(this._classSurveySettingKey))
                                    {
                                        await deleteClassSurvey(false);
                                    }
                                    else
                                    {
                                        this.dialogClient.show(RightShipDialogServiceConstants.URLs.ConfirmDeleteDialog, {
                                            entity: "class Survey",
                                            delete: async (dontShowWarningAgain: boolean) => {
                                                await deleteClassSurvey(dontShowWarningAgain);
                                                if(dontShowWarningAgain && this.userClientService && this.userClientService.currentUser && this.userClientService.currentUser.settings)
                                                {
                                                    this.userClientService.currentUser.settings[this._classSurveySettingKey] = 'true';
                                                }
                                                this.dialogClient.hide();
                                            }
                                        }, true, false, false);
                                    }
                                },
                                item: { text: "Delete" },
                                classList: ''
                            }
                        ]
                    }
                }
            });

        this.classSurveysItemSource = {
            sourceType: SourceType.Collection,
            source: classSurveys,
            columns: columns,
            initialFilters: []
        };
        this.lastClassSurveyDate = !classSurveys || classSurveys.length == 0 ? 'N/A' : moment(Enumerable.from(classSurveys).orderByDescending(x => x.createdDate).first().createdDate).format("DD/MM/YYYY HH:mm");
    }

    private userHasSetting(settingName: string): boolean
    {
        if(this.userClientService && this.userClientService.currentUser && this.userClientService.currentUser.settings)
        {
            const settings = this.userClientService.currentUser.settings;
            const settingKeys = Object.keys(settings).map(x => x.toLowerCase());
            if(settingKeys && settingKeys.indexOf(settingName.toLowerCase()) > -1)
            {
                return settings[settingName] && settings[settingName].toLowerCase() == "true";
            }
        }

        return false;
    }

    private setClassCertificatesItemSource(classCertificates: VesselClassCertificateClientModel[]) {
        const columns = [];
        columns.push(
            {
                columnOrder: 1,
                sortOrder: 1,
                dataType: DataType.Default,
                isSortable: true,
                name: 'classificationSociety',
                title: 'Class'
            },
            {
                columnOrder: 1,
                sortOrder: 1,
                dataType: DataType.Default,
                isSortable: true,
                name: 'name',
                title: 'Name'
            },
            {
                columnOrder: 1,
                sortOrder: 1,
                dataType: DataType.Default,
                isSortable: true,
                name: 'certificateType',
                title: 'Type'
            },
            {
                columnOrder: 1,
                sortOrder: 1,
                dataType: DataType.Date,
                isSortable: true,
                name: 'issuedDate',
                title: 'Issued Date',
                valueConverter: (value) => value ? moment(value).format("DD/MM/YYYY") : ""
            },
            {
                columnOrder: 1,
                sortOrder: 1,
                dataType: DataType.Date,
                isSortable: true,
                name: 'expiryDate',
                title: 'Expiry Date',
                valueConverter: (value) => value ? moment(value).format("DD/MM/YYYY") : ""
            }, 
            {
                columnOrder: 1,
                sortOrder: 1,
                dataType: DataType.Date,
                isSortable: true,
                name: 'postponedUntilDate',
                title: 'Postponed Until',
                valueConverter: (value) => value ? moment(value).format("DD/MM/YYYY") : ""
            },
            {
                columnOrder: 10,
                sortOrder: 1,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                name: "menu",
                value: `<div class="actions row justify-content-end align-items-middle">
                            <list-menu hide-menu-border="true" menu-items.bind="menuItems(item)" if.bind="menuItems(item).length > 0">
                                <template replace-part="menu-item">
                                    <div class="row nowrap align-items-middle" click.trigger="menuItem.click($event, item)">
                                        <span class="text">\${menuItem.item.text}</span>
                                    </div>  
                                </template>
                            </list-menu>
                        </div>`,
                parameter: {
                    item: null,
                    menuItems: () => {
                        return [
                            {
                                isVisible: true,
                                click: ($event, item) => {
                                    if ($event)
                                        $event.stopPropagation();
                                    this.dialogClient.show(RightShipDialogServiceConstants.URLs.AddVesselClassCertificateDialog, {
                                        onComplete: this.handleSaveClassCertificateComplete.bind(this),
                                        id: item ? item.vesselClassCertificateId : null,
                                        vesselId: this.model.id ? this.model.id : null
                                    }, true, false, false, 'right');
                                },
                                item: { text: "Edit" },
                                classList: ''
                            },
                            {
                                isVisible: true,
                                click: async ($event, item) => {
                                    if ($event && $event.stopPropagation)
                                        $event.stopPropagation();

                                    const deleteClassCertificate = async (dontShowWarningAgain: boolean) => {
                                        const response = await this.vesselService.deleteClassCertificate(item.vesselClassCertificateId, dontShowWarningAgain);
                                        if (response.isSuccess) {
                                            const index = this.classCertificatesItemSource.source.indexOf(item);
                                            this.classCertificatesItemSource.source.splice(index, 1);
                                        }
                                    }
                                    if(this.userHasSetting(this._classCertificateSettingKey))
                                    {
                                        await deleteClassCertificate(false);
                                    }
                                    else
                                    {
                                        this.dialogClient.show(RightShipDialogServiceConstants.URLs.ConfirmDeleteDialog, {
                                            entity: "class Certificates",
                                            delete: async (dontShowWarningAgain: boolean) => {
                                                await deleteClassCertificate(dontShowWarningAgain);
                                                if(dontShowWarningAgain && this.userClientService && this.userClientService.currentUser && this.userClientService.currentUser.settings)
                                                {
                                                    this.userClientService.currentUser.settings[this._classCertificateSettingKey] = 'true';
                                                }
                                                this.dialogClient.hide();
                                            }
                                        }, true, false, false);
                                    }
                                },
                                item: { text: "Delete" },
                                classList: ''
                            }
                        ]
                    }
                }
            });

        this.classCertificatesItemSource = {
            sourceType: SourceType.Collection,
            source: classCertificates,
            columns: columns,
            initialFilters: []
        };
        this.lastClassCertificateDate = !classCertificates || classCertificates.length == 0 ? 'N/A' : moment(Enumerable.from(classCertificates).orderByDescending(x => x.createdDate).first().createdDate).format("DD/MM/YYYY HH:mm");
    }

    private getUscgItemSource(data: BaseUscgData[]) {

        const columns = [];
        columns.push(
            {
                columnOrder: 1,
                sortOrder: 1,
                dataType: DataType.Default,
                isSortable: true,
                name: 'externalId',
                title: 'Activity Id'
            },
            {
                columnOrder: 1,
                sortOrder: 1,
                dataType: DataType.Default,
                isSortable: true,
                name: 'type',
                title: 'Type'
            },
            {
                columnOrder: 1,
                sortOrder: 1,
                dataType: DataType.Default,
                isSortable: true,
                name: 'category',
                title: 'category'
            },
            {
                columnOrder: 1,
                sortOrder: 1,
                dataType: DataType.Default,
                isSortable: true,
                name: 'unit',
                title: 'Unit'
            },
            {
                columnOrder: 1,
                sortOrder: 1,
                dataType: DataType.Default,
                isSortable: true,
                name: 'reason',
                title: 'Reason'
            },
            {
                columnOrder: 1,
                sortOrder: 1,
                dataType: DataType.Date,
                isSortable: true,
                name: 'imposedDateTime',
                title: 'Imposed',
                valueConverter: x => x ? moment(new Date(x)).format("DD MMM YY HH:mm") : null

            },
            {
                columnOrder: 1,
                sortOrder: 1,
                dataType: DataType.Date,
                isSortable: true,
                name: 'removedDateTime',
                title: 'Removed',
                valueConverter: x => x ? moment(new Date(x)).format("DD MMM YY HH:mm") : null

            },
            {
                columnOrder: 1,
                sortOrder: 1,
                dataType: DataType.Default,
                isSortable: true,
                name: 'comments',
                title: 'Comments'
            },
            {
                columnOrder: 1,
                sortOrder: 1,
                dataType: DataType.Boolean,
                isSortable: true,
                name: 'isClosed',
                title: 'Reviewed'
            },
            {
                columnOrder: 4,
                isHidden: (item) => { return !item || item.isClosed; },
                sortOrder: 1,
                dataType: DataType.CustomTemplate,
                value: `<a class="link" click.trigger="showUscgCloseoutDialog(item)">Close out</a>`,
                instancePerRow: true,
                name: "closeOut",
                parameter: {
                    item: null,
                    showUscgCloseoutDialog: (item) => this.showUscgCloseoutDialog(item)
                }
            }
        );

        return {
            sourceType: SourceType.Collection,
            source: data,
            columns: columns,
            initialFilters: []
        };
    }

    private setCAPSurveysItemSource(capSurveys: VesselCapSurveyClientModel[]) {
        const columns = [];
        columns.push(
            {
                columnOrder: 1,
                sortOrder: 1,
                dataType: DataType.Default,
                isSortable: true,
                name: 'issuedBy',
                title: 'Issued By'
            },
            {
                columnOrder: 2,
                sortOrder: 0,
                dataType: DataType.DateTime,
                isSortable: true,
                name: 'dateIssued',
                title: 'Date Issued',
                valueConverter: x => x ? moment(new Date(x)).format("DD MMM YYYY") : null
            },
            {
                columnOrder: 3,
                sortOrder: 1,
                dataType: DataType.DateTime,
                isSortable: true,
                name: 'completedDate',
                title: 'Completed Date',
                valueConverter: x => x ? moment(new Date(x)).format("DD MMM YYYY") : null
            },
            {
                columnOrder: 4,
                sortOrder: 1,
                dataType: DataType.Default,
                isSortable: true,
                name: 'hullRating',
                title: 'Hull Rating'
            },
            {
                columnOrder: 5,
                sortOrder: 1,
                dataType: DataType.Default,
                isSortable: true,
                name: 'machineryCargoSystemRating',
                title: 'Machinery & Cargo System Rating'
            },
            {
                columnOrder: 6,
                sortOrder: 1,
                dataType: DataType.DateTime,
                isSortable: true,
                name: 'machineryCargoSystemRatingCompletedDate',
                title: 'Machinery & Cargo System Rating Completed Date',
                valueConverter: x => x ? moment(new Date(x)).format("DD MMM YYYY") : null
            },
            {
                columnOrder: 7,
                sortOrder: 1,
                dataType: DataType.Default,
                isSortable: true,
                name: 'source',
                title: 'Source'
            },
            {
                columnOrder: 10,
                sortOrder: 1,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                name: "menu",
                value: `<div class="actions row justify-content-end align-items-middle">
                            <list-menu hide-menu-border="true" menu-items.bind="menuItems(item)" if.bind="menuItems(item).length > 0">
                                <template replace-part="menu-item">
                                    <div class="row nowrap align-items-middle" click.trigger="menuItem.click($event, item)">
                                        <span class="text">\${menuItem.item.text}</span>
                                    </div>  
                                </template>
                            </list-menu>
                        </div>`,
                parameter: {
                    item: null,
                    menuItems: () => {
                        return [
                            {
                                isVisible: true,
                                click: ($event, item) => {
                                    if ($event)
                                        $event.stopPropagation();
                                    this.dialogClient.show(RightShipDialogServiceConstants.URLs.AddVesselCapSurveyDialog, {
                                        onComplete: this.handleSaveCAPSurveyComplete.bind(this),
                                        id: item ? item.vesselCapSurveyId : null,
                                        vesselId: this.model.id ? this.model.id : null
                                    }, true, false, false, 'right');
                                },
                                item: { text: "Edit" },
                                classList: ''
                            },
                            {
                                isVisible: true,
                                click: ($event, item) => {
                                    if ($event)
                                        $event.stopPropagation();
                                    this.dialogClient.show(RightShipDialogServiceConstants.URLs.DeleteModal, {
                                        delete: async () => {
                                            const response = await this.vesselService.deleteCAPSurvey(item.vesselCapSurveyId);
                                            if (response.isSuccess) {
                                                const index = this.capSurveysItemSource.source.indexOf(item);
                                                this.capSurveysItemSource.source.splice(index, 1);
                                            }
                                            this.dialogClient.hide();
                                        }
                                    }, true, false, false, 'right');
                                },
                                item: { text: "Delete" },
                                classList: ''
                            },
                            {
                                isVisible: true,
                                click: ($event, item) => {
                                    if ($event)
                                        $event.stopPropagation();
                                    this.onHistoryClick(item.vesselCapSurveyId, EntityTypes.CAPSurvey);
                                },
                                item: { text: "History" },
                                classList: ''
                            }
                        ]
                    }
                }
            });

        this.capSurveysItemSource = {
            sourceType: SourceType.Collection,
            source: capSurveys,
            columns: columns,
            initialFilters: []
        };
    }

    public addCapSurvey() {
        this.dialogClient.show(RightShipDialogServiceConstants.URLs.AddVesselCapSurveyDialog, {
            onComplete: this.handleSaveCAPSurveyComplete.bind(this),
            id: null,
            vesselId: this.model.id ? this.model.id : null
        }, true, false, false, 'right');
    }

    handleSaveCAPSurveyComplete(item: VesselCapSurveyClientModel, action: number) {
        if (action == UnitOfWorkAction.Added) {
            this.capSurveysItemSource.source.push(item);
        }
        else if (action == UnitOfWorkAction.Modified) {
            const index = ArrayUtils.findIndex(this.capSurveysItemSource.source, x => x.vesselCapSurveyId == item.vesselCapSurveyId)
            this.capSurveysItemSource.source.splice(index, 1, item);
        }
    }

    private setThirdPartyInspectionsItemSource(thirdPartyInspections: VesselThirdPartyInspectionClientModel[]) {

        const columns = [];
        columns.push(
            {
                columnOrder: 1,
                sortOrder: 1,
                dataType: DataType.Default,
                isSortable: true,
                name: 'thirdPartyInspectionType',
                title: 'Inspection Type'
            },
            {
                columnOrder: 2,
                sortOrder: 1,
                dataType: DataType.Default,
                isSortable: true,
                name: 'inspectingCompany',
                title: 'Inspecting Company'
            },
            {
                columnOrder: 3,
                sortOrder: 0,
                dataType: DataType.DateTime,
                isSortable: true,
                name: 'inspectionDate',
                title: 'Date of Inspection',
                valueConverter: x => x ? moment(new Date(x)).format("DD MMM YYYY") : null
            },
            {
                columnOrder: 4,
                sortOrder: 1,
                dataType: DataType.Default,
                isSortable: true,
                name: 'vesselOperationAtTimeOfInspection',
                title: "Vessel's operation at the time of the inspection"
            },
            {
                columnOrder: 5,
                sortOrder: 1,
                dataType: DataType.Default,
                isSortable: true,
                name: 'productBeingHandled',
                title: 'Product being handled'
            },
            {
                columnOrder: 6,
                sortOrder: 1,
                dataType: DataType.Boolean,
                isSortable: true,
                name: 'satisfactoryWithOperatorComments',
                title: "Satisfactory with operator's comments"
            },
            {
                columnOrder: 7,
                sortOrder: 1,
                dataType: DataType.Default,
                isSortable: true,
                name: 'comments',
                title: 'Comments',
                width: '400px'
            },
            {
                columnOrder: 10,
                sortOrder: 1,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                name: "menu",
                value: `<div class="actions row justify-content-end align-items-middle">
                            <list-menu hide-menu-border="true" menu-items.bind="menuItems(item)" if.bind="menuItems(item).length > 0">
                                <template replace-part="menu-item">
                                    <div class="row nowrap align-items-middle" click.trigger="menuItem.click($event, item)">
                                        <span class="text">\${menuItem.item.text}</span>
                                    </div>  
                                </template>
                            </list-menu>
                        </div>`,
                parameter: {
                    item: null,
                    menuItems: () => {
                        return [
                            {
                                isVisible: true,
                                click: ($event, item) => {
                                    if ($event)
                                        $event.stopPropagation();
                                    this.dialogClient.show(RightShipDialogServiceConstants.URLs.AddVesselThirdPartyInspectionDialog, {
                                        onComplete: this.handleSaveThirdPartyInspectionComplete.bind(this),
                                        id: item ? item.vesselThirdPartyInspectionId : null,
                                        vesselId: this.model.id ? this.model.id : null
                                    }, true, false, false, 'right');
                                },
                                item: { text: "Edit" },
                                classList: ''
                            },
                            {
                                isVisible: true,
                                click: ($event, item) => {
                                    if ($event)
                                        $event.stopPropagation();
                                    this.dialogClient.show(RightShipDialogServiceConstants.URLs.DeleteModal, {
                                        delete: async () => {
                                            const response = await this.vesselService.deleteThirdPartyInspection(item.vesselThirdPartyInspectionId);
                                            if (response.isSuccess) {
                                                const index = this.thirdPartyInspectionsItemSource.source.indexOf(item);
                                                this.thirdPartyInspectionsItemSource.source.splice(index, 1);
                                            }
                                            this.dialogClient.hide();
                                        }
                                    }, true, false, false, 'right');
                                },
                                item: { text: "Delete" },
                                classList: ''
                            },
                            {
                                isVisible: true,
                                click: ($event, item) => {
                                    if ($event)
                                        $event.stopPropagation();
                                    this.onHistoryClick(item.vesselThirdPartyInspectionId, EntityTypes.ThirdPartyInspection);
                                },
                                item: { text: "History" },
                                classList: ''
                            }
                        ]
                    }
                }
            });

        this.thirdPartyInspectionsItemSource = {
            sourceType: SourceType.Collection,
            source: thirdPartyInspections,
            columns: columns,
            initialFilters: []
        };
    }

    public addThirdPartyInspection() {
        this.dialogClient.show(RightShipDialogServiceConstants.URLs.AddVesselThirdPartyInspectionDialog, {
            onComplete: this.handleSaveThirdPartyInspectionComplete.bind(this),
            id: null,
            vesselId: this.model.id ? this.model.id : null
        }, true, false, false, 'right');
    }

    handleSaveThirdPartyInspectionComplete(item: VesselThirdPartyInspectionClientModel, action: number) {
        if (action == UnitOfWorkAction.Added) {
            this.thirdPartyInspectionsItemSource.source.push(item);
        }
        else if (action == UnitOfWorkAction.Modified) {
            const index = ArrayUtils.findIndex(this.thirdPartyInspectionsItemSource.source, x => x.vesselThirdPartyInspectionId == item.vesselThirdPartyInspectionId)
            this.thirdPartyInspectionsItemSource.source.splice(index, 1, item);
        }
    }

    public onHistoryClick(id: number = null, entityType: number = null) {
        this.dialogClient.show(
            RightShipDialogServiceConstants.URLs.VettingCriteriaHistoryTrail, { id: id ? id : this.model.id, entityType: entityType }, true, false, false, 'right');
    }

    public save() {
        return new Promise<void>((resolve, reject) => {
            const validator = this.viewModel.getValidator(this.model);

            // If validator is null, it means save is called without having the manager activated first.
            if (validator == null) {
                resolve();
            }

            validator.validate().then(validResponse => {
                if (validResponse.valid) {

                    const newEntityDynamicValues = this.viewModel.getChangedEntityDynamicValues();

                    if (newEntityDynamicValues.length > 0) {
                        this.vesselService.saveDynamicPropertiesForEntity(newEntityDynamicValues, true).then(response => {
                            this.viewModel.onDynamicPropertiesFetched(response);
                            this.notificationService.show(this.i18N.tr('changes-saved-successfully-text'), '', NotificationType.Success, 5000);
                            resolve();
                        }).catch(reject);
                    }
                    else {
                        this.notificationService.show("No Changes", this.i18N.tr("there-were-no-changes-to-save-text"), NotificationType.Info, 5000);
                    }

                } else {
                    reject();
                }
            });
        });
    }

    private setMousItemSource(vesselMous: VesselMouClientModel[]) {

        const columns = [];
        columns.push(
            {
                columnOrder: 1,
                sortOrder: 1,
                dataType: DataType.Default,
                isSortable: true,
                name: 'mouName',
                title: 'MOU'
            },
            {
                columnOrder: 2,
                sortOrder: 1,
                dataType: DataType.DateTime,
                isSortable: true,
                name: 'lastVisitedDate',
                title: 'Last Visited Date',
                valueConverter: x => x ? moment(new Date(x)).format("DD MMM YYYY") : null
            },
            {
                columnOrder: 3,
                sortOrder: 1,
                dataType: DataType.DateTime,
                isSortable: true,
                name: 'lastClearedDate',
                title: 'Last Cleared Date',
                valueConverter: x => x ? moment(new Date(x)).format("DD MMM YYYY") : null
            });

        this.mousItemSource = {
            sourceType: SourceType.Collection,
            source: vesselMous,
            columns: columns,
            initialFilters: []
        };
    }

    private setMouClearanceLogsItemSource() {

        const columns = [];
        columns.push(
            {
                columnOrder: 1,
                sortOrder: 1,
                dataType: DataType.Default,
                isSortable: true,
                name: 'mou',
                title: 'MOU',
                valueConverter: x => this.i18N.tr(x)
            },
            {
                columnOrder: 2,
                sortOrder: 0,
                dataType: DataType.DateTime,
                isSortable: true,
                name: 'clearedDate',
                title: 'Date',
                isAscending: false,
                valueConverter: x => x ? moment(new Date(x)).format("DD MMM YYYY HH:mm:ss") : null
            },
            {
                columnOrder: 3,
                sortOrder: 1,
                dataType: DataType.Default,
                isSortable: true,
                name: 'user',
                title: 'User'
            });

        this.mouClearanceLogsItemSource = {
            sourceType: SourceType.ODataUrl,
            source: "odata/VesselMouClearanceLogs?$filter=vesselId eq " + this.model.id,
            columns: columns,
            initialFilters: []
        };
    }

    handleMousCleared = (param: { vesselId: number, mou: VesselMousEnum, isSuccessFull: boolean }) => {
        if (param && param.isSuccessFull && param.vesselId && param.vesselId == this.model.id && this.mouClearanceLogsDataGridVM) {
            this.mouClearanceLogsDataGridVM.reloadData();
        }
    }

    public pageCountSet: any[] = [
        { label: 10, value: 10 },
        { label: 50, value: 50 },
        { label: 100, value: 100 },
    ];

    setPageCount(datagridVM: RightshipDataGrid, pageCount: { label: string, value: number }) {
        datagridVM.pageCount = pageCount.value;
    }

    @computedFrom(Reflection.getPropertyPath<VesselDetailsVettingAttributes>(x => x.mouClearanceLogsDataGridVM.itemSource), Reflection.getPropertyPath<VesselDetailsVettingAttributes>(x => x.mouClearanceLogsDataGridVM.itemSource.source),
        Reflection.getPropertyPath<VesselDetailsVettingAttributes>(x => x.mouClearanceLogsDataGridVM.page), Reflection.getPropertyPath<VesselDetailsVettingAttributes>(x => x.mouClearanceLogsDataGridVM.pageCount),
        Reflection.getPropertyPath<VesselDetailsVettingAttributes>(x => x.mouClearanceLogsDataGridVM.itemSource.source.length), Reflection.getPropertyPath<VesselDetailsVettingAttributes>(x => x.mouClearanceLogsDataGridVM.unfilteredCount))
    public get clearanceLogDisplayingItemsText() {
        if (this.mouClearanceLogsDataGridVM) {
            let unfilteredCount = '0';
            if (this.mouClearanceLogsDataGridVM.itemSource && Array.isArray(this.mouClearanceLogsDataGridVM.itemSource.source) && this.mouClearanceLogsDataGridVM.itemSource.source.length)
                unfilteredCount = this.mouClearanceLogsDataGridVM.itemSource.source.length.toString();
            else if (this.mouClearanceLogsDataGridVM.unfilteredCount && this.mouClearanceLogsDataGridVM.unfilteredCount > 0)
                unfilteredCount = this.mouClearanceLogsDataGridVM.unfilteredCount.toString();
            const currentCount = (this.mouClearanceLogsDataGridVM.page + 1) * this.mouClearanceLogsDataGridVM.pageCount;
            const current = currentCount > Number(unfilteredCount) ? unfilteredCount : currentCount.toString();
            return this.i18N.tr('displaying-items-text').replace("{0}", (this.mouClearanceLogsDataGridVM.page == 0 ? 1 : (this.mouClearanceLogsDataGridVM.page * this.mouClearanceLogsDataGridVM.pageCount) + 1).toString()).replace("{1}", current).replace("{2}", unfilteredCount).replace("{3}", "logs");
        }
        else {
            return "";
        }
    }

    mouClearanceLogsDataGridVMChanged() {
        if (this.mouClearanceLogsDataGridVM) {
            this.mouClearanceLogsDataGridVM.pageCount = 10;
        }
    }

    showUscgCloseoutDialog = (item) => {
        this.dialogClient.show(RightShipDialogServiceConstants.URLs.UscgCloseOutDialog,
            {
                item: item, reload: () => this.loadUscgData(this.model.id)
            }, true, false, false, "right");
    }

    @computedFrom(Reflection.getPropertyPath<VesselDetailsVettingAttributes>(x => x.classSurveysDataGridVM.itemSource), Reflection.getPropertyPath<VesselDetailsVettingAttributes>(x => x.classSurveysDataGridVM.itemSource.source),
        Reflection.getPropertyPath<VesselDetailsVettingAttributes>(x => x.classSurveysDataGridVM.page), Reflection.getPropertyPath<VesselDetailsVettingAttributes>(x => x.classSurveysDataGridVM.pageCount),
        Reflection.getPropertyPath<VesselDetailsVettingAttributes>(x => x.classSurveysDataGridVM.itemSource.source.length), Reflection.getPropertyPath<VesselDetailsVettingAttributes>(x => x.classSurveysDataGridVM.unfilteredCount))
    public get classSurveyDisplayingItemsText() {
        if (this.classSurveysDataGridVM) {
            let unfilteredCount = '0';
            if (this.classSurveysDataGridVM.itemSource && Array.isArray(this.classSurveysDataGridVM.itemSource.source) && this.classSurveysDataGridVM.itemSource.source.length)
                unfilteredCount = this.classSurveysDataGridVM.itemSource.source.length.toString();
            else if (this.classSurveysDataGridVM.unfilteredCount && this.classSurveysDataGridVM.unfilteredCount > 0)
                unfilteredCount = this.classSurveysDataGridVM.unfilteredCount.toString();
            const currentCount = (this.classSurveysDataGridVM.page + 1) * this.classSurveysDataGridVM.pageCount;
            const current = currentCount > Number(unfilteredCount) ? unfilteredCount : currentCount.toString();
            return this.i18N.tr('displaying-items-text').replace("{0}", (this.classSurveysDataGridVM.page == 0 ? 1 : (this.classSurveysDataGridVM.page * this.classSurveysDataGridVM.pageCount) + 1).toString()).replace("{1}", current).replace("{2}", unfilteredCount).replace("{3}", "Class Surveys");
        }
        else {
            return "";
        }
    }

    handleSaveClassSurveyComplete(item: VesselClassSurveyClientModel, action: number) {
        if (action == UnitOfWorkAction.Added) {
            this.classSurveysItemSource.source.push(item);
        }
        else if (action == UnitOfWorkAction.Modified) {
            const index = ArrayUtils.findIndex(this.classSurveysItemSource.source, x => x.vesselClassSurveyId == item.vesselClassSurveyId)
            this.classSurveysItemSource.source.splice(index, 1, item);
        }
    }

    public addClassSurvey()
    {
        this.dialogClient.show(RightShipDialogServiceConstants.URLs.AddVesselClassSurveyDialog, {
            onComplete: this.handleSaveClassSurveyComplete.bind(this),
            id: null,
            vesselId: this.model.id ? this.model.id : null
        }, true, false, false, 'right');
    }

    @computedFrom(Reflection.getPropertyPath<VesselDetailsVettingAttributes>(x => x.classCertificatesDataGridVM.itemSource), Reflection.getPropertyPath<VesselDetailsVettingAttributes>(x => x.classCertificatesDataGridVM.itemSource.source),
        Reflection.getPropertyPath<VesselDetailsVettingAttributes>(x => x.classCertificatesDataGridVM.page), Reflection.getPropertyPath<VesselDetailsVettingAttributes>(x => x.classCertificatesDataGridVM.pageCount),
        Reflection.getPropertyPath<VesselDetailsVettingAttributes>(x => x.classCertificatesDataGridVM.itemSource.source.length), Reflection.getPropertyPath<VesselDetailsVettingAttributes>(x => x.classCertificatesDataGridVM.unfilteredCount))
    public get classCertificateDisplayingItemsText() {
        if (this.classCertificatesDataGridVM) {
            let unfilteredCount = '0';
            if (this.classCertificatesDataGridVM.itemSource && Array.isArray(this.classCertificatesDataGridVM.itemSource.source) && this.classCertificatesDataGridVM.itemSource.source.length)
                unfilteredCount = this.classCertificatesDataGridVM.itemSource.source.length.toString();
            else if (this.classCertificatesDataGridVM.unfilteredCount && this.classCertificatesDataGridVM.unfilteredCount > 0)
                unfilteredCount = this.classCertificatesDataGridVM.unfilteredCount.toString();
            const currentCount = (this.classCertificatesDataGridVM.page + 1) * this.classCertificatesDataGridVM.pageCount;
            const current = currentCount > Number(unfilteredCount) ? unfilteredCount : currentCount.toString();
            return this.i18N.tr('displaying-items-text').replace("{0}", (this.classCertificatesDataGridVM.page == 0 ? 1 : (this.classCertificatesDataGridVM.page * this.classCertificatesDataGridVM.pageCount) + 1).toString()).replace("{1}", current).replace("{2}", unfilteredCount).replace("{3}", "Class Certificates");
        }
        else {
            return "";
        }
    }

    handleSaveClassCertificateComplete(item: VesselClassCertificateClientModel, action: number) {
        if (action == UnitOfWorkAction.Added) {
            this.classCertificatesItemSource.source.push(item);
        }
        else if (action == UnitOfWorkAction.Modified) {
            const index = ArrayUtils.findIndex(this.classCertificatesItemSource.source, x => x.vesselClassCertificateId == item.vesselClassCertificateId)
            this.classCertificatesItemSource.source.splice(index, 1, item);
        }
    }

    public addClassCertificate()
    {
        this.dialogClient.show(RightShipDialogServiceConstants.URLs.AddVesselClassCertificateDialog, {
            onComplete: this.handleSaveClassCertificateComplete.bind(this),
            id: null,
            vesselId: this.model.id ? this.model.id : null
        }, true, false, false, 'right');
    }
}
